import { useCallback, useEffect, useRef, useState } from "react";
import { Copy, FilePlus2, MessageSquareShare, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatPrice } from "@/lib/store";
import { useRates } from "@/lib/pricing";
import { EMPTY_PROPOSAL, buildMessage, canSave, clientWaLink, copyText, fromSaved, proposalTotals, toPayload } from "@/lib/proposal";
import { pInput, ghostBtn } from "./ItemRows";
import { ProposalWanted } from "./ProposalWanted";
import { ProposalTrade } from "./ProposalTrade";
import { ProposalPayment } from "./ProposalPayment";
import { ProposalHistory } from "./ProposalHistory";

const Result = ({ t }) => (
  <p data-testid="proposal-result" className={`rounded-2xl px-4 py-3 text-sm ${t.change > 0 ? "bg-[#25D366]/15" : "bg-white/[0.06]"}`}>
    {t.change > 0 ? <>Cliente recebe de volta: <strong className="text-[#25D366]">{formatPrice(t.change)}</strong></>
      : t.hasTrade ? <>Diferença a pagar: <strong className="text-white">{t.pay > 0 ? formatPrice(t.pay) : "R$ 0,00"}</strong></>
        : <>Total: <strong className="text-white">{formatPrice(t.pay)}</strong></>}
  </p>
);

// Área só do lojista: monta compra, troca ou downgrade e manda a proposta pronta no WhatsApp do cliente.
export const ProposalBuilder = ({ products }) => {
  useRates();
  const [p, setP] = useState(EMPTY_PROPOSAL);
  const [list, setList] = useState([]);
  const top = useRef(null);
  const set = (patch) => setP((s) => ({ ...s, ...patch }));
  const load = useCallback(() => api.get("/admin/proposals").then((r) => setList(Array.isArray(r?.data) ? r.data : [])).catch(() => setList([])), []);
  useEffect(() => { load(); }, [load]);

  const t = proposalTotals(p);
  const message = buildMessage(p);
  const ok = canSave(p);

  const persist = async () => {
    const body = toPayload(p, message);
    const { data } = p.id ? await api.put(`/admin/proposals/${p.id}`, body) : await api.post("/admin/proposals", body);
    set({ id: data.id });
    return data;
  };
  const run = async (fn, msg) => {
    try {
      await fn();
      if (msg) toast.success(msg);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    load();
  };
  const send = () => {
    window.open(clientWaLink(p.client_phone, message), "_blank");
    run(async () => { const d = await persist(); await api.post(`/admin/proposals/${d.id}/sent`); }, "Proposta salva e WhatsApp aberto");
  };
  const copy = () => run(async () => { await copyText(message); await persist(); }, "Mensagem copiada e proposta salva");
  const resend = (d) => {
    window.open(clientWaLink(d.client_phone, d.message), "_blank");
    run(() => api.post(`/admin/proposals/${d.id}/sent`));
  };
  const edit = (d) => {
    setP(fromSaved(d));
    top.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const remove = (d) => {
    if (!window.confirm(`Apagar a proposta de ${d.client_name || "cliente sem nome"}?`)) return;
    if (d.id === p.id) setP(EMPTY_PROPOSAL);
    run(() => api.delete(`/admin/proposals/${d.id}`), "Proposta apagada");
  };

  return (
    <section ref={top} data-testid="admin-proposals" className="mt-8 scroll-mt-6 rounded-[24px] border border-white/10 bg-[#111113] p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5"><MessageSquareShare className="h-5 w-5 text-white" /><h2 className="font-display text-xl font-bold">Montar proposta</h2></div>
        {p.id && <span data-testid="proposal-editing" className="rounded-full bg-white/10 px-3 py-1 text-xs text-zinc-200">Editando proposta salva</span>}
      </div>
      <p className="mt-2 text-sm text-zinc-400">Monte uma compra, troca ou downgrade para o cliente e mande pronta no WhatsApp dele. Nada aparece no site.</p>
      <div className="mt-5 grid gap-6 lg:grid-cols-[1fr_380px]">
        <div className="min-w-0 space-y-6">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm text-zinc-300">Nome do cliente
              <input data-testid="proposal-client-name" value={p.client_name} maxLength={80} placeholder="Ex: Ana" onChange={(e) => set({ client_name: e.target.value })} className={`${pInput} mt-1.5`} />
            </label>
            <label className="text-sm text-zinc-300">WhatsApp do cliente
              <input data-testid="proposal-client-phone" inputMode="tel" value={p.client_phone} placeholder="(35) 99999-9999" onChange={(e) => set({ client_phone: e.target.value.replace(/[^\d()+\s-]/g, "").slice(0, 25) })} className={`${pInput} mt-1.5`} />
            </label>
          </div>
          <ProposalWanted items={p.wanted} onChange={(wanted) => set({ wanted })} products={products} />
          <ProposalTrade items={p.trade} onChange={(trade) => set({ trade })} />
          {ok && <Result t={t} />}
          <ProposalPayment amount={t.pay} cash={p.cash} installments={p.installments} onChange={set} />
          <label className="block text-sm text-zinc-300">Observação <span className="text-zinc-400">(opcional, vai no fim da mensagem)</span>
            <textarea data-testid="proposal-notes" value={p.notes} maxLength={600} rows={2} placeholder="Ex: Valor válido até sexta. Entrega grátis em Areado." onChange={(e) => set({ notes: e.target.value })} className={`${pInput} mt-1.5 resize-none`} />
          </label>
        </div>
        <div className="min-w-0 lg:sticky lg:top-6 lg:self-start">
          <p className="text-sm font-semibold text-white">Mensagem para o cliente</p>
          <pre data-testid="proposal-message-preview" className="mt-2 max-h-[420px] overflow-y-auto whitespace-pre-wrap break-words rounded-2xl border border-white/10 bg-black/40 p-4 font-sans text-sm leading-relaxed text-zinc-100" data-lenis-prevent>
            {ok ? message : "Adicione o aparelho que o cliente vai levar para ver a mensagem."}
          </pre>
          <p className="mt-2 text-xs text-zinc-400">{p.client_phone.replace(/\D/g, "") ? "O WhatsApp abre direto na conversa do cliente." : "Sem número, o WhatsApp abre para você escolher o contato."}</p>
          <div className="mt-3 grid gap-2">
            <button type="button" data-testid="proposal-send" disabled={!ok} onClick={send} className="flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3 text-sm font-semibold text-black transition-transform hover:scale-[1.02] disabled:opacity-40"><MessageSquareShare className="h-4 w-4" /> Salvar e abrir WhatsApp</button>
            <div className="grid grid-cols-3 gap-2">
              <button type="button" data-testid="proposal-copy" disabled={!ok} onClick={copy} className={`${ghostBtn} justify-center`}><Copy className="h-3.5 w-3.5" /> Copiar</button>
              <button type="button" data-testid="proposal-save" disabled={!ok} onClick={() => run(persist, "Proposta salva")} className={`${ghostBtn} justify-center`}><Save className="h-3.5 w-3.5" /> Salvar</button>
              <button type="button" data-testid="proposal-new" onClick={() => setP(EMPTY_PROPOSAL)} className={`${ghostBtn} justify-center`}><FilePlus2 className="h-3.5 w-3.5" /> Nova</button>
            </div>
          </div>
        </div>
      </div>
      <ProposalHistory list={list} onResend={resend} onCopy={(d) => copyText(d.message).then(() => toast.success("Mensagem copiada"))} onEdit={edit} onDelete={remove} />
    </section>
  );
};
